<template>
  <div :class="fullView ? 'full-view' : 'claims-trend-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { BarChart, LineChart } from 'echarts/charts'
import { COLOR_PALETTE, getDefaultChartTextStyle, getBarNoDataOptions } from '@/helpers/insightsCharts'
import { lightenDarkenColor } from '@/helpers'
import { SVGRenderer } from 'echarts/renderers'
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components'
import { use } from 'echarts/core'
import { useChartResize } from '@/composables/useChartResize'
import VChart from 'vue-echarts'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, GridComponent, LegendComponent, BarChart, LineChart, SVGRenderer])

export default {
  name: 'ClaimsTrendChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: [Object, Array],
      required: true,
      default: () => ([])
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()
    return {
      chartRef,
      handleResize
    }
  },
  data () {
    const agreedValueLabel = this.$t('insights.charts.claimsTrend.agreedValue')
    const estimatedValueLabel = this.$t('insights.charts.claimsTrend.estimatedValue')
    const volumeLabel = this.$t('insights.charts.claimsTrend.volume')

    return {
      option: {
        animation: !this.fullView,
        title: {
          text: this.$t('insights.charts.claimsTrend.title'),
          top: 0,
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            if (!params?.length) return ''
            let tooltip = `${params[0].axisValueLabel}<br/>`
            params.forEach(p => {
              const isValue = p.seriesType === 'bar'
              tooltip += `${p.marker} ${p.seriesName}: ${isValue ? Number(p.value).toLocaleString() : p.value}<br/>`
            })
            return tooltip
          }
        },
        legend: {
          data: [agreedValueLabel, estimatedValueLabel, volumeLabel],
          bottom: '0',
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          containLabel: true
        },
        xAxis: [{
          type: 'category',
          data: [],
          axisTick: { alignWithLabel: true },
          axisLabel: {
            interval: 0,
            rotate: 90,
            fontSize: 10,
            show: this.fullView
          }
        }],
        yAxis: [
          {
            type: 'value',
            name: 'Value',
            position: 'left',
            axisLine: { lineStyle: { color: COLOR_PALETTE[0] } },
            axisLabel: { formatter: '{value}' }
          },
          {
            type: 'value',
            name: 'Volume',
            position: 'right',
            axisLine: { lineStyle: { color: COLOR_PALETTE[2] } },
            axisLabel: { formatter: '{value}' }
          }
        ],
        series: [
          {
            name: agreedValueLabel,
            type: 'bar',
            yAxisIndex: 0,
            barWidth: '30%',
            data: [],
            itemStyle: {
              color: COLOR_PALETTE[0],
              borderWidth: 1,
              borderColor: lightenDarkenColor(COLOR_PALETTE[0], -50)
            }
          },
          {
            name: estimatedValueLabel,
            type: 'bar',
            yAxisIndex: 0,
            barWidth: '30%',
            data: [],
            itemStyle: {
              color: COLOR_PALETTE[1],
              borderWidth: 1,
              borderColor: lightenDarkenColor(COLOR_PALETTE[1], -10)
            }
          },
          {
            name: volumeLabel,
            type: 'line',
            yAxisIndex: 1,
            data: [],
            showSymbol: false,
            itemStyle: { color: COLOR_PALETTE[2] }
          }
        ]
      }
    }
  },
  methods: {
    updateData () {
      const noDataOptions = getBarNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        Object.assign(this.option, noDataOptions)
        this.option.title.text = this.$t('insights.charts.claimsTrend.title')
        emitChartFinished(this, true)
        return
      }

      if (!this.resultSet.chartPivot) {
        this.option.xAxis[0].data = []
        this.option.series[0].data = []
        this.option.series[1].data = []
        this.option.series[2].data = []
        return
      }

      const rows = this.resultSet.chartPivot().map(item => {
        const rawDate = item.xValues?.[4]
        const currency = item.xValues?.[5] || ''
        if (!rawDate) return null

        const dateStr = rawDate
          .replace(/(\.\d{3})\d+/, '$1')
          .replace(/([+-]\d{2})$/, '$1:00')

        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return null

        const month = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`

        const agreed = Number(item['variations_parts.agreedvaluecents']) || 0
        const estimated = Number(item['variations_parts.estimatedvaluecents']) || 0
        const volume = Number(item['variations.count'] || 0)

        return { month, currency, agreed, estimated, volume }
      }).filter(Boolean)

      const grouped = {}
      rows.forEach(r => {
        const key = `${r.month}|${r.currency}`
        if (!grouped[key]) grouped[key] = { month: r.month, currency: r.currency, agreed: 0, estimated: 0, volume: 0 }
        grouped[key].agreed += r.agreed
        grouped[key].estimated += r.estimated
        grouped[key].volume += r.volume
      })

      const filtered = Object.values(grouped).filter(g => g.volume > 0)

      const sorted = filtered.sort((a, b) => {
        if (a.month === b.month) return a.currency.localeCompare(b.currency)
        return a.month.localeCompare(b.month)
      })

      const categories = sorted.map(d => `${d.month} ${d.currency}`)
      const agreedValues = sorted.map(d => d.agreed / 100)
      const estimatedValues = sorted.map(d => d.estimated / 100)
      const volumes = sorted.map(d => d.volume)

      this.option.xAxis[0].data = categories
      this.option.series[0].data = agreedValues
      this.option.series[1].data = estimatedValues
      this.option.series[2].data = volumes
      this.option.series[2].show = volumes.some(v => v > 0)

      const minValue = Math.min(...agreedValues, ...estimatedValues)
      const maxValue = Math.max(...agreedValues, ...estimatedValues)
      const maxVolume = Math.max(...volumes)

      this.option.yAxis[0].min = undefined
      this.option.yAxis[0].max = undefined
      this.option.yAxis[1].min = undefined
      this.option.yAxis[1].max = undefined

      if (minValue < 0 && maxValue > 0 && maxVolume > 0) {
        const rangeValue = maxValue - minValue
        const zeroPosition = (0 - minValue) / rangeValue
        const rightMin = (zeroPosition * maxVolume) / (zeroPosition - 1)

        this.option.yAxis[0].min = minValue
        this.option.yAxis[0].max = maxValue
        this.option.yAxis[1].min = rightMin
        this.option.yAxis[1].max = maxVolume

        this.option.yAxis[1].axisLabel = {
          formatter: (v) => (v < 0 ? '' : v)
        }
      } else {
        this.option.yAxis[0].min = minValue >= 0 ? 0 : undefined
        this.option.yAxis[1].min = 0
        if (this.option.yAxis[1].axisLabel.formatter) {
          delete this.option.yAxis[1].axisLabel.formatter
        }
      }
      emitChartFinished(this, false)
    }
  },
  mounted () {
    this.updateData()
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.claims-trend-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
